import React, { useState } from 'react'
import { Box, Grid, makeStyles, Paper, Switch, Typography } from '@material-ui/core'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import { toast } from 'utils/notify'
import * as yup from 'yup'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import moment from 'moment'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import { updateConferenceById } from 'store/conference'
import ProgressButton from 'components/ProgressButton'
import ErrorMessage from 'components/ErrorMessage'
import ConfirmationModal from 'components/ConfirmationModal'
import CustomTextField from 'components/Form/CustomTextField'

const validationSchema = yup.object().shape({
  enableAttendance: yup.boolean(),
  attendanceStartTime: yup.string().when('enableAttendance', {
    is: true,
    then: yup.string().required('Start Time is required'),
    otherwise: yup.string()
  }),
  attendanceEndTime: yup.string().when('enableAttendance', {
    is: true,
    then: yup
      .string()
      .required('End Time is required')
      .test(
        'greater than start time',
        'End Time must be greater than Start Time',
        (value, context) => {
          const startValue = context.parent.attendanceStartTime
          const enableAttendance = context.parent.enableAttendance
          return enableAttendance
            ? new Date(value || 0).getTime() > new Date(startValue).getTime()
            : true
        }
      ),
    otherwise: yup.string()
  }),
  attendanceInfoMessage: yup.string().when('enableAttendance', {
    is: true,
    then: yup.string().required('Attendance Explanation Message is required'),
    otherwise: yup.string()
  }),
  defaultGraceTime: yup.number().when('enableAttendance', {
    is: true,
    then: yup
      .number()
      .typeError('Grace Time must be a number')
      .integer('Grace Time must be a whole number of minutes')
      .min(0, 'Grace Time cannot be negative')
      .required('Grace Time is required'),
    otherwise: yup.number().nullable()
  })
})

const EducationCreditsForm = ({ conference }) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false)
  const [pendingEnableState, setPendingEnableState] = useState(null)

  const initialValues = {
    enableAttendance: conference?.enableAttendance || false,
    attendanceStartTime: conference?.attendanceStartTime
      ? moment(conference?.attendanceStartTime, 'hh:mm A')
      : moment('08:00 AM', 'hh:mm A'),
    attendanceEndTime: conference?.attendanceEndTime
      ? moment(conference?.attendanceEndTime, 'hh:mm A')
      : moment('08:00 PM', 'hh:mm A'),
    attendanceInfoMessage: conference?.attendanceInfoMessage || '',
    defaultGraceTime:
      conference?.defaultGraceTime !== undefined && conference?.defaultGraceTime !== null
        ? conference.defaultGraceTime
        : 15
  }

  const handleToggleChange = (setFieldValue, currentValue) => {
    if (currentValue && !pendingEnableState && currentValue !== !conference?.enableAttendance) {
      // If we're trying to disable and it's currently enabled, show confirmation dialog
      setShowDisableConfirmation(true)
      setPendingEnableState(false)
    } else {
      // Otherwise, just enable it directly
      setFieldValue('enableAttendance', !currentValue)
    }
  }

  const handleConfirmDisable = async (setFieldValue) => {
    setFieldValue('enableAttendance', false)
    setShowDisableConfirmation(false)
    setPendingEnableState(null)

    try {
      setLoading(true)
      await dispatch(
        updateConferenceById(conference.id, {
          enableAttendance: false,
          attendanceStartTime: null,
          attendanceEndTime: null,
          attendanceInfoMessage: '',
          defaultGraceTime: null
        })
      )
      toast.success('Attendance module disabled.')
    } catch (error) {
      toast.error('Could not disable attendance module.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDisable = () => {
    setShowDisableConfirmation(false)
    setPendingEnableState(null)
  }

  const handleSubmit = async (values) => {
    if (!conference?.id) {
      toast.error('Conference ID is missing. Reload and try again.')
      return
    }

    const payload = {
      enableAttendance: values.enableAttendance,
      attendanceStartTime: values.enableAttendance
        ? moment(values.attendanceStartTime).format('hh:mm A')
        : null,
      attendanceEndTime: values.enableAttendance
        ? moment(values.attendanceEndTime).format('hh:mm A')
        : null,
      attendanceInfoMessage: values.enableAttendance ? values.attendanceInfoMessage : '',
      defaultGraceTime: values.enableAttendance ? Number(values.defaultGraceTime) : null
    }

    try {
      setLoading(true)
      await dispatch(updateConferenceById(conference.id, payload))
      toast.success('Attendance settings updated.')
    } catch (error) {
      toast.error('Could not update attendance settings.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper className={classes.section}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        enableReinitialize>
        {({ setFieldValue, values, errors, touched }) => (
          <Form>
            <Box
              className={classes.sectionTitle}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
              <Typography variant="h6" color="secondary" style={{ fontWeight: 'bold' }}>
                Attendance
              </Typography>
              <Switch
                color="primary"
                name="enableAttendance"
                onChange={() => handleToggleChange(setFieldValue, values.enableAttendance)}
                checked={values.enableAttendance}
              />
            </Box>

            {values.enableAttendance ? (
              <>
                <Typography variant="subtitle2" className={classes.label}>
                  Attendance Check-In Time slot
                </Typography>
                <Typography variant="body2" className={classes.subText}>
                  Set a specific time period for attendees to mark their attendance to receive
                  educational credits
                </Typography>

                <Grid container spacing={2} className={classes.timePickerContainer}>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label="Start Time"
                        value={values.attendanceStartTime}
                        onChange={(newValue) => {
                          setFieldValue('attendanceStartTime', newValue)
                        }}
                        name="attendanceStartTime"
                        slotProps={{
                          textField: {
                            readOnly: true,
                            fullWidth: true,
                            placeholder: `hh:mm ${!conference?.is24hrFormat ? 'A' : ''}`,
                            error: !!(touched.attendanceStartTime && errors.attendanceStartTime),
                            helperText:
                              touched.attendanceStartTime && errors.attendanceStartTime
                                ? errors.attendanceStartTime
                                : ''
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label="End Time"
                        value={values.attendanceEndTime}
                        onChange={(newValue) => {
                          setFieldValue('attendanceEndTime', newValue)
                        }}
                        name="attendanceEndTime"
                        slotProps={{
                          textField: {
                            readOnly: true,
                            fullWidth: true,
                            placeholder: `hh:mm ${!conference?.is24hrFormat ? 'A' : ''}`,
                            error: !!(touched.attendanceEndTime && errors.attendanceEndTime),
                            helperText:
                              touched.attendanceEndTime && errors.attendanceEndTime
                                ? errors.attendanceEndTime
                                : ''
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <Box className={classes.messageContainer}>
                  <Typography variant="subtitle2" className={classes.label}>
                    Grace Time (minutes)
                  </Typography>
                  <Typography variant="body2" className={classes.subText}>
                    Admin-configured window, before and after a target&apos;s start time within
                    which a scan auto-approves. Configured once per Conference and applied globally
                    to every Event/Session.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        fullWidth
                        type="number"
                        label="Grace Time (minutes)"
                        name="defaultGraceTime"
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" className={classes.noteText}>
                    Scans outside the grace time window are still recorded as attendance, but must
                    be manually approved or rejected by an admin.
                  </Typography>
                </Box>

                <Box className={classes.messageContainer}>
                  <Typography variant="subtitle2" className={classes.label}>
                    Attendance Explanation Message
                  </Typography>
                  <Typography variant="body2" className={classes.subText}>
                    This text appears as helpful guidelines for attendees when marking their
                    attendance
                  </Typography>
                  <TipTapEditor
                    hasPlaceholders={false}
                    value={values.attendanceInfoMessage}
                    onChange={(value) => setFieldValue('attendanceInfoMessage', value)}
                    placeholder="Write attendance info message here..."
                  />
                  <ErrorMessage
                    message={touched?.attendanceInfoMessage && errors?.attendanceInfoMessage}
                  />

                  <Box className={classes.actions}>
                    <ProgressButton
                      type="submit"
                      label="Save Changes"
                      inProgress={loading}
                      disabled={loading}
                      color="primary"
                      variant="contained"
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Typography style={{ textAlign: 'center' }}>
                This module is currently disabled. Toggle the switch to enable it.
              </Typography>
            )}

            <ConfirmationModal
              open={showDisableConfirmation}
              dialogTitle="Disable Attendance Module"
              dialogText="Are you sure you want to disable the attendance module? Attendees will no longer be able to mark attendance for educational credits."
              closeModal={handleCancelDisable}
              handleConfirm={() => handleConfirmDisable(setFieldValue)}
            />
          </Form>
        )}
      </Formik>
    </Paper>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  timePickerContainer: {
    marginTop: '16px',
    marginBottom: '16px'
  },
  messageContainer: {
    marginTop: '24px'
  },
  label: {
    fontSize: '15px',
    fontWeight: 500,
    marginBottom: '8px'
  },
  subText: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '12px'
  },
  noteText: {
    fontSize: '13px',
    color: '#8a6d3b',
    marginTop: '8px',
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px'
  },
  timePicker: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px'
    }
  }
})

export default EducationCreditsForm
